import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { MagnifyingGlass, HardDrives, Package } from "@phosphor-icons/react";
import { PublicNav } from "@/components/layout/PublicNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { ProductCard } from "@/components/agent/ProductCard";
import { NewsletterBox, useTitle } from "@/components/growth";
import { api } from "@/lib/api";

const COPY = {
  model: {
    eyebrow: "Modelos locales", icon: HardDrives, title: "IA que corre en tu máquina.",
    sub: "Modelos personalizados y especializados de HackLabs. Privacidad total, sin nube, sin cuotas por token.",
    perks: [["100% privado", "Tus datos nunca salen de tu equipo."], ["Sin costes por uso", "Paga una vez, ejecuta sin límites."], ["Compatibles", "Ollama, LM Studio, llama.cpp y más."]],
  },
  software: {
    eyebrow: "Software", icon: Package, title: "Herramientas profesionales.",
    sub: "Programas desarrollados por HackLabs para empresas, profesionales y entusiastas. Licencia única por compra.",
    perks: [["Licencia incluida", "Clave única en tu biblioteca."], ["Descarga inmediata", "Acceso al instante tras el pago."], ["Actualizaciones", "Historial de versiones transparente."]],
  },
};

export default function ProductsCatalog({ kind }) {
  const c = COPY[kind];
  useTitle(c.eyebrow);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const { data: meta } = useQuery({ queryKey: ["pmeta"], queryFn: async () => (await api.get("/products/meta")).data, staleTime: Infinity });
  const { data: items = [], isLoading } = useQuery({ queryKey: ["products", kind, q, category], queryFn: async () => (await api.get("/products", { params: { kind, q, category } })).data });
  const cats = kind === "model" ? meta?.model_categories : meta?.software_categories;
  const I = c.icon;

  return (
    <div className="noise min-h-screen bg-[#050505] text-white">
      <PublicNav />
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="grid-bg absolute inset-0" />
        <div className="relative mx-auto max-w-7xl px-6 pb-10 pt-16">
          <div className="reveal flex items-center gap-3"><I size={20} weight="duotone" className="text-cyan-400" /><p className="eyebrow !text-cyan-400">{c.eyebrow}</p></div>
          <h1 className="reveal mt-4 font-display text-4xl font-extrabold tracking-tighter sm:text-5xl lg:text-6xl" data-testid="catalog-title">{c.title}</h1>
          <p className="reveal mt-4 max-w-2xl text-zinc-400">{c.sub}</p>
          <div className="mt-8 grid max-w-3xl gap-px border border-white/10 bg-white/10 sm:grid-cols-3">
            {c.perks.map(([t, d]) => <div key={t} className="bg-[#070707] p-4"><p className="text-sm font-semibold">{t}</p><p className="mt-1 text-xs text-zinc-500">{d}</p></div>)}
          </div>
          <div className="mt-10 flex items-center gap-3 border border-white/10 bg-[#0d0d0d] px-4 focus-within:border-blue-500">
            <MagnifyingGlass size={18} className="text-zinc-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o etiqueta…" className="h-12 flex-1 bg-transparent text-sm focus:outline-none" data-testid="catalog-search-input" />
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {["", ...(cats || [])].map((x) => (
              <button key={x || "all"} onClick={() => setCategory(x)} className={`border px-3 py-1.5 text-xs ${category === x ? "border-blue-500 bg-blue-600 text-white" : "border-white/10 text-zinc-400 hover:text-white"}`} data-testid={`catalog-category-${x ? x.toLowerCase().replace(/\s+/g, "-") : "all"}`}>{x || "Todas"}</button>
            ))}
          </div>
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-6 py-12">
        {isLoading ? <div className="h-px bg-white/10 thinking-beam" /> : items.length === 0 ? (
          <div className="border border-dashed border-white/15 p-16" data-testid="catalog-empty">
            <p className="font-display text-2xl font-bold">Próximamente.</p>
            <p className="mt-2 text-sm text-zinc-500">Estamos preparando el catálogo. Suscríbete y te avisamos en el lanzamiento.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 [&>a]:border [&>a]:border-white/10" data-testid="catalog-grid">
            {items.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
          </div>
        )}
        <div className="mt-16"><NewsletterBox source={`catalog-${kind}`} /></div>
      </section>
      <SiteFooter />
    </div>
  );
}
